import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from '../../../../core/services/api.service';
import { ProductConfigService } from '../../../../core/services/product-config.service';
import {
  MostRecentAttemptsAPI,
  MostRecentAttemptsPayload,
  ScheduledTodayAPI,
  ScheduledTodayPayload,
  PendingByGapAPI,
  PendingByGapPayload,
  CreationTrendAPI,
  CreationTrendPayload,
  ScheduledTrendAPI,
  ScheduledTrendPayload,
  PaymentOutcomesBySlotAPI,
  PaymentOutcomesBySlotPayload,
  SuccessRateTrendAPI,
  SuccessRateTrendPayload,
  RecoveryStatusAPI,
  RecoveryStatusPayload,
  ByAdminAPI,
  ByAdminPayload,
  RecoveryRateAPI,
  RecoveryRatePayload,
  AvgRecoveryAmountAPI,
  AvgRecoveryAmountPayload,
  ProcessingTimeAPI,
  ProcessingTimePayload,
  AdminDetailAPI,
  AdminDetailPayload,
  DuplicatesSummaryStatsAPI,
  DuplicatesSummaryStatsPayload,
  DuplicatesByAdminAPI,
  DuplicatesByAdminPayload,
  DuplicatesTrendAPI,
  DuplicatesTrendPayload,
  ContractDetailsAPI,
  ContractDetailsPayload,
} from '../interface/arrangment-cron-api.interface';
import { environment } from '../../../../../environments/environment';

/**
 * Enterprise-grade API service for Arrangement Cron operations.
 * Provides typed methods for interacting with arrangement cron endpoints.
 * 
 * @class ArrangmentCronApiService
 */
@Injectable({
  providedIn: 'root',
})
export class ArrangmentCronApiService {
  private readonly BASE_ENDPOINT = 'visionboard/tools/arrangements';
  private readonly DEFAULT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

  constructor(
    private apiService: ApiService,
    private productConfigService: ProductConfigService
  ) { }

  private getProductContext() {
    const productId = this.apiService.getSelectedProductId() || sessionStorage.getItem('selected_product_id');
    const effectiveProductId = productId || 'denefits';
    const productName = this.productConfigService.getProductName(effectiveProductId);
    const config = this.productConfigService.getProductConfig(productName);

    let productServerName = config.productServerName;
    // if (['denefits', 'credee'].includes(config.id) && !environment.production) {
    //   productServerName = `${config.id}-live`;
    // }

    return { config, productServerName };
  }

  private mapResponse(response: any) {
    if (!response || typeof response !== 'object') {
      throw new Error('Invalid response format from arrangements API');
    }

    if (response.is_error == 1) {
      console.error('Error retrieving data:', response);
      throw new Error(response.err || 'Something went wrong');
    }

    return response.hasOwnProperty('data') ? response.data : response;
  }

  /**
   * Retrieves most recent arrangement execution attempts from yesterday.
   * Shows arrangement execution attempts grouped by time slot and payment method.
   * 
   * @param payload - Request payload with timezone and is_dummy
   * @param options - Optional configuration for the API request
   * @returns Observable that emits the most recent attempts data
   */
  getMostRecentAttempts(
    payload: MostRecentAttemptsPayload,
    options: {
      showLoader?: boolean;
      cache?: boolean | number;
      showErrorToaster?: boolean;
    } = {}
  ): Observable<MostRecentAttemptsAPI> {
    const {
      showLoader = false,
      cache = false,
      showErrorToaster = true,
    } = options;

    const cacheTTL = typeof cache === 'number' ? cache : this.DEFAULT_CACHE_TTL;

    const cleanPayload = {
      timezone: payload.timezone,
      is_dummy: payload.is_dummy,
    };

    const { config, productServerName } = this.getProductContext();

    return this.apiService
      .post<MostRecentAttemptsAPI>(
        `${this.BASE_ENDPOINT}/most_recent`,
        { ...cleanPayload, ...config.payloadExtras },
        {
          addAccessToken: config.requiresAccessToken,
          cache: cache ? cacheTTL : false,
          productServerName,
          showLoader,
          showErrorToaster,
          errorMessage: 'Failed to retrieve most recent attempts',
        }
      )
      .pipe(
        map((response) => {
          return this.mapResponse(response);
        }),
        catchError((error: any) => {
          const errorMessage =
            error?.message ||
            error?.error ||
            'An unexpected error occurred while retrieving most recent attempts';
          console.error('Error retrieving most recent attempts:', error);
          return throwError(() => ({
            ...error,
            message: errorMessage,
            originalError: error,
          }));
        })
      );
  }

  /**
   * Retrieves arrangements scheduled for today, organized by time slot.
   * Lists all arrangements scheduled for today (Morning/Afternoon/Evening).
   * 
   * @param payload - Request payload with timezone and is_dummy
   * @param options - Optional configuration for the API request
   * @returns Observable that emits the scheduled today data
   */
  getScheduledToday(
    payload: ScheduledTodayPayload,
    options: {
      showLoader?: boolean;
      cache?: boolean | number;
      showErrorToaster?: boolean;
    } = {}
  ): Observable<ScheduledTodayAPI> {
    const {
      showLoader = false,
      cache = false,
      showErrorToaster = true,
    } = options;

    const cacheTTL = typeof cache === 'number' ? cache : this.DEFAULT_CACHE_TTL;

    const cleanPayload = {
      timezone: payload.timezone,
      is_dummy: payload.is_dummy,
    };

    const { config, productServerName } = this.getProductContext();

    return this.apiService
      .post<ScheduledTodayAPI>(
        `${this.BASE_ENDPOINT}/scheduled_today`,
        { ...cleanPayload, ...config.payloadExtras },
        {
          addAccessToken: config.requiresAccessToken,
          cache: cache ? cacheTTL : false,
          productServerName,
          showLoader,
          showErrorToaster,
          errorMessage: 'Failed to retrieve scheduled today data',
        }
      )
      .pipe(
        map((response) => {
          return this.mapResponse(response);
        }),
        catchError((error: any) => {
          const errorMessage =
            error?.message ||
            error?.error ||
            'An unexpected error occurred while retrieving scheduled today data';
          console.error('Error retrieving scheduled today data:', error);
          return throwError(() => ({
            ...error,
            message: errorMessage,
            originalError: error,
          }));
        })
      );
  }

  /**
   * Retrieves pending arrangements grouped by days until execution.
   * Groups pending arrangements by gap (1-30, 31-60, 61-90, 90+ days).
   * 
   * @param payload - Request payload with timezone, is_dummy, and include_records
   * @param options - Optional configuration for the API request
   * @returns Observable that emits the pending by gap data
   */
  getPendingByGap(
    payload: PendingByGapPayload,
    options: {
      showLoader?: boolean;
      cache?: boolean | number;
      showErrorToaster?: boolean;
    } = {}
  ): Observable<PendingByGapAPI> {
    const {
      showLoader = false,
      cache = false,
      showErrorToaster = true,
    } = options;

    const cacheTTL = typeof cache === 'number' ? cache : this.DEFAULT_CACHE_TTL;

    const cleanPayload = {
      timezone: payload.timezone,
      is_dummy: payload.is_dummy,
      include_records: payload.include_records ?? false,
    };

    const { config, productServerName } = this.getProductContext();

    return this.apiService
      .post<PendingByGapAPI>(
        `${this.BASE_ENDPOINT}/pending_by_gap`,
        { ...cleanPayload, ...config.payloadExtras },
        {
          addAccessToken: config.requiresAccessToken,
          cache: cache ? cacheTTL : false,
          productServerName,
          showLoader,
          showErrorToaster,
          errorMessage: 'Failed to retrieve pending by gap data',
        }
      )
      .pipe(
        map((response) => {
          return this.mapResponse(response);
        }),
        catchError((error: any) => {
          const errorMessage =
            error?.message ||
            error?.error ||
            'An unexpected error occurred while retrieving pending by gap data';
          console.error('Error retrieving pending by gap data:', error);
          return throwError(() => ({
            ...error,
            message: errorMessage,
            originalError: error,
          }));
        })
      );
  }

  /**
   * Retrieves arrangement creation trends over time.
   * Shows arrangement creation trends grouped by date unit (day/week/month).
   * 
   * @param payload - Request payload with date range, timezone, unit, is_dummy, include, outputFormat
   * @param options - Optional configuration for the API request
   * @returns Observable that emits the creation trend data
   */
  getCreationTrend(
    payload: CreationTrendPayload,
    options: {
      showLoader?: boolean;
      cache?: boolean | number;
      showErrorToaster?: boolean;
    } = {}
  ): Observable<CreationTrendAPI> {
    const {
      showLoader = false,
      cache = false,
      showErrorToaster = true,
    } = options;

    const cacheTTL = typeof cache === 'number' ? cache : this.DEFAULT_CACHE_TTL;

    const cleanPayload = Object.entries(payload)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .reduce((obj, [key, value]) => {
        obj[key as keyof any] = value as any;
        return obj;
      }, {} as any);

    if (!cleanPayload.outputFormat) {
      cleanPayload.outputFormat = 'db';
    }

    const { config, productServerName } = this.getProductContext();

    return this.apiService
      .post<CreationTrendAPI>(
        `${this.BASE_ENDPOINT}/creation_trend`,
        { ...cleanPayload, ...config.payloadExtras },
        {
          addAccessToken: config.requiresAccessToken,
          cache: cache ? cacheTTL : false,
          productServerName,
          showLoader,
          showErrorToaster,
          errorMessage: 'Failed to retrieve creation trend data',
        }
      )
      .pipe(
        map((response) => {
          response = this.mapResponse(response);
          if (!response.stats || !Array.isArray(response.records)) {
            throw new Error('Invalid response structure: missing stats or records');
          }
          return response;
        }),
        catchError((error: any) => {
          const errorMessage =
            error?.message ||
            error?.error ||
            'An unexpected error occurred while retrieving creation trend data';
          console.error('Error retrieving creation trend data:', error);
          return throwError(() => ({
            ...error,
            message: errorMessage,
            originalError: error,
          }));
        })
      );
  }

  /**
   * Retrieves scheduled arrangements trend over time.
   * Shows scheduled arrangements based on execution date, grouped by date unit.
   * 
   * @param payload - Request payload with date range, timezone, unit, is_dummy, include, outputFormat
   * @param options - Optional configuration for the API request
   * @returns Observable that emits the scheduled trend data
   */
  getScheduledTrend(
    payload: ScheduledTrendPayload,
    options: {
      showLoader?: boolean;
      cache?: boolean | number;
      showErrorToaster?: boolean;
    } = {}
  ): Observable<ScheduledTrendAPI> {
    const {
      showLoader = false,
      cache = false,
      showErrorToaster = true,
    } = options;

    const cacheTTL = typeof cache === 'number' ? cache : this.DEFAULT_CACHE_TTL;

    const cleanPayload = Object.entries(payload)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .reduce((obj, [key, value]) => {
        obj[key as keyof any] = value as any;
        return obj;
      }, {} as any);

    if (!cleanPayload.outputFormat) {
      cleanPayload.outputFormat = 'db';
    }

    const { config, productServerName } = this.getProductContext();

    return this.apiService
      .post<ScheduledTrendAPI>(
        `${this.BASE_ENDPOINT}/scheduled_trend`,
        { ...cleanPayload, ...config.payloadExtras },
        {
          addAccessToken: config.requiresAccessToken,
          cache: cache ? cacheTTL : false,
          productServerName,
          showLoader,
          showErrorToaster,
          errorMessage: 'Failed to retrieve scheduled trend data',
        }
      )
      .pipe(
        map((response) => {
          response = this.mapResponse(response);
          if (!response.stats || !Array.isArray(response.records)) {
            throw new Error('Invalid response structure: missing stats or records');
          }
          return response;
        }),
        catchError((error: any) => {
          const errorMessage =
            error?.message ||
            error?.error ||
            'An unexpected error occurred while retrieving scheduled trend data';
          console.error('Error retrieving scheduled trend data:', error);
          return throwError(() => ({
            ...error,
            message: errorMessage,
            originalError: error,
          }));
        })
      );
  }

  /**
   * Retrieves payment outcomes by time slot over a date range.
   * Shows success and failure rates by time slot with recovery amounts.
   * 
   * @param payload - Request payload with date range, timezone, unit, is_dummy, include, outputFormat
   * @param options - Optional configuration for the API request
   * @returns Observable that emits the payment outcomes by slot data
   */
  getPaymentOutcomesBySlot(
    payload: PaymentOutcomesBySlotPayload,
    options: {
      showLoader?: boolean;
      cache?: boolean | number;
      showErrorToaster?: boolean;
    } = {}
  ): Observable<PaymentOutcomesBySlotAPI> {
    const {
      showLoader = false,
      cache = false,
      showErrorToaster = true,
    } = options;

    const cacheTTL = typeof cache === 'number' ? cache : this.DEFAULT_CACHE_TTL;

    const cleanPayload = Object.entries(payload)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .reduce((obj, [key, value]) => {
        obj[key as keyof any] = value as any;
        return obj;
      }, {} as any);

    if (!cleanPayload.outputFormat) {
      cleanPayload.outputFormat = 'db';
    }

    const { config, productServerName } = this.getProductContext();

    return this.apiService
      .post<PaymentOutcomesBySlotAPI>(
        `${this.BASE_ENDPOINT}/payment_outcomes_by_slot`,
        { ...cleanPayload, ...config.payloadExtras },
        {
          addAccessToken: config.requiresAccessToken,
          cache: cache ? cacheTTL : false,
          productServerName,
          showLoader,
          showErrorToaster,
          errorMessage: 'Failed to retrieve payment outcomes by slot data',
        }
      )
      .pipe(
        map((response) => {
          response = this.mapResponse(response);
          if (!response.stats || !Array.isArray(response.records)) {
            throw new Error('Invalid response structure: missing stats or records');
          }
          return response;
        }),
        catchError((error: any) => {
          const errorMessage =
            error?.message ||
            error?.error ||
            'An unexpected error occurred while retrieving payment outcomes by slot data';
          console.error('Error retrieving payment outcomes by slot data:', error);
          return throwError(() => ({
            ...error,
            message: errorMessage,
            originalError: error,
          }));
        })
      );
  }

  /**
   * Retrieves success rate trends over time.
   * Shows success rate trends with breakdowns by payment type and time slot.
   * 
   * @param payload - Request payload with date range, timezone, unit, is_dummy, include, outputFormat
   * @param options - Optional configuration for the API request
   * @returns Observable that emits the success rate trend data
   */
  getSuccessRateTrend(
    payload: SuccessRateTrendPayload,
    options: {
      showLoader?: boolean;
      cache?: boolean | number;
      showErrorToaster?: boolean;
    } = {}
  ): Observable<SuccessRateTrendAPI> {
    const {
      showLoader = false,
      cache = false,
      showErrorToaster = true,
    } = options;

    const cacheTTL = typeof cache === 'number' ? cache : this.DEFAULT_CACHE_TTL;

    const cleanPayload = Object.entries(payload)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .reduce((obj, [key, value]) => {
        obj[key as keyof any] = value as any;
        return obj;
      }, {} as any);

    if (!cleanPayload.outputFormat) {
      cleanPayload.outputFormat = 'db';
    }

    const { config, productServerName } = this.getProductContext();

    return this.apiService
      .post<SuccessRateTrendAPI>(
        `${this.BASE_ENDPOINT}/success_rate_trend`,
        { ...cleanPayload, ...config.payloadExtras },
        {
          addAccessToken: config.requiresAccessToken,
          cache: cache ? cacheTTL : false,
          productServerName,
          showLoader,
          showErrorToaster,
          errorMessage: 'Failed to retrieve success rate trend data',
        }
      )
      .pipe(
        map((response) => {
          response = this.mapResponse(response);
          if (!response.stats || !Array.isArray(response.records)) {
            throw new Error('Invalid response structure: missing stats or records');
          }
          return response;
        }),
        catchError((error: any) => {
          const errorMessage =
            error?.message ||
            error?.error ||
            'An unexpected error occurred while retrieving success rate trend data';
          console.error('Error retrieving success rate trend data:', error);
          return throwError(() => ({
            ...error,
            message: errorMessage,
            originalError: error,
          }));
        })
      );
  }

  /**
   * Retrieves recovery status breakdown over time.
   * Shows recovered vs non-recovered amounts and counts.
   * 
   * @param payload - Request payload with date range, timezone, unit, is_dummy, include, outputFormat
   * @param options - Optional configuration for the API request
   * @returns Observable that emits the recovery status data
   */
  getRecoveryStatus(
    payload: RecoveryStatusPayload,
    options: {
      showLoader?: boolean;
      cache?: boolean | number;
      showErrorToaster?: boolean;
    } = {}
  ): Observable<RecoveryStatusAPI> {
    const {
      showLoader = false,
      cache = false,
      showErrorToaster = true,
    } = options;

    const cacheTTL = typeof cache === 'number' ? cache : this.DEFAULT_CACHE_TTL;

    const cleanPayload = Object.entries(payload)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .reduce((obj, [key, value]) => {
        obj[key as keyof any] = value as any;
        return obj;
      }, {} as any);

    if (!cleanPayload.outputFormat) {
      cleanPayload.outputFormat = 'db';
    }

    const { config, productServerName } = this.getProductContext();

    return this.apiService
      .post<RecoveryStatusAPI>(
        `${this.BASE_ENDPOINT}/recovery_status`,
        { ...cleanPayload, ...config.payloadExtras },
        {
          addAccessToken: config.requiresAccessToken,
          cache: cache ? cacheTTL : false,
          productServerName,
          showLoader,
          showErrorToaster,
          errorMessage: 'Failed to retrieve recovery status data',
        }
      )
      .pipe(
        map((response) => {
          response = this.mapResponse(response);
          if (!response.stats || !Array.isArray(response.records)) {
            throw new Error('Invalid response structure: missing stats or records');
          }
          return response;
        }),
        catchError((error: any) => {
          const errorMessage =
            error?.message ||
            error?.error ||
            'An unexpected error occurred while retrieving recovery status data';
          console.error('Error retrieving recovery status data:', error);
          return throwError(() => ({
            ...error,
            message: errorMessage,
            originalError: error,
          }));
        })
      );
  }

  /**
   * Retrieves arrangement statistics grouped by admin.
   * Shows arrangement statistics with performance scores and efficiency metrics.
   * 
   * @param payload - Request payload with date range, timezone, is_dummy, include
   * @param options - Optional configuration for the API request
   * @returns Observable that emits the by admin data
   */
  getByAdmin(
    payload: ByAdminPayload,
    options: {
      showLoader?: boolean;
      cache?: boolean | number;
      showErrorToaster?: boolean;
    } = {}
  ): Observable<ByAdminAPI> {
    const {
      showLoader = false,
      cache = false,
      showErrorToaster = true,
    } = options;

    const cacheTTL = typeof cache === 'number' ? cache : this.DEFAULT_CACHE_TTL;

    const cleanPayload = Object.entries(payload)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .reduce((obj, [key, value]) => {
        obj[key as keyof any] = value as any;
        return obj;
      }, {} as any);

    const { config, productServerName } = this.getProductContext();

    return this.apiService
      .post<ByAdminAPI>(
        `${this.BASE_ENDPOINT}/by_admin`,
        { ...cleanPayload, ...config.payloadExtras },
        {
          addAccessToken: config.requiresAccessToken,
          cache: cache ? cacheTTL : false,
          productServerName,
          showLoader,
          showErrorToaster,
          errorMessage: 'Failed to retrieve by admin data',
        }
      )
      .pipe(
        map((response) => {
          response = this.mapResponse(response);
          if (!response.stats || !Array.isArray(response.records)) {
            throw new Error('Invalid response structure: missing stats or records');
          }
          return response;
        }),
        catchError((error: any) => {
          const errorMessage =
            error?.message ||
            error?.error ||
            'An unexpected error occurred while retrieving by admin data';
          console.error('Error retrieving by admin data:', error);
          return throwError(() => ({
            ...error,
            message: errorMessage,
            originalError: error,
          }));
        })
      );
  }

  /**
   * Retrieves recovery rate trend over time.
   * Shows overall recovery rate as percentage of recovered amount vs total overdue amount.
   * 
   * @param payload - Request payload with date range, timezone, unit, is_dummy, outputFormat
   * @param options - Optional configuration for the API request
   * @returns Observable that emits the recovery rate data
   */
  getRecoveryRate(
    payload: RecoveryRatePayload,
    options: {
      showLoader?: boolean;
      cache?: boolean | number;
      showErrorToaster?: boolean;
    } = {}
  ): Observable<RecoveryRateAPI> {
    const {
      showLoader = false,
      cache = false,
      showErrorToaster = true,
    } = options;

    const cacheTTL = typeof cache === 'number' ? cache : this.DEFAULT_CACHE_TTL;

    const cleanPayload = Object.entries(payload)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .reduce((obj, [key, value]) => {
        obj[key as keyof any] = value as any;
        return obj;
      }, {} as any);

    if (!cleanPayload.outputFormat) {
      cleanPayload.outputFormat = 'db';
    }

    const { config, productServerName } = this.getProductContext();

    return this.apiService
      .post<RecoveryRateAPI>(
        `${this.BASE_ENDPOINT}/recovery_rate`,
        { ...cleanPayload, ...config.payloadExtras },
        {
          addAccessToken: config.requiresAccessToken,
          cache: cache ? cacheTTL : false,
          productServerName,
          showLoader,
          showErrorToaster,
          errorMessage: 'Failed to retrieve recovery rate data',
        }
      )
      .pipe(
        map((response) => {
          response = this.mapResponse(response);
          if (!response.stats || !Array.isArray(response.records)) {
            throw new Error('Invalid response structure: missing stats or records');
          }
          return response;
        }),
        catchError((error: any) => {
          const errorMessage =
            error?.message ||
            error?.error ||
            'An unexpected error occurred while retrieving recovery rate data';
          console.error('Error retrieving recovery rate data:', error);
          return throwError(() => ({
            ...error,
            message: errorMessage,
            originalError: error,
          }));
        })
      );
  }

  /**
   * Retrieves average recovery amount per successful arrangement.
   * Shows average amount recovered with breakdown by payment type.
   * 
   * @param payload - Request payload with date range, timezone, is_dummy
   * @param options - Optional configuration for the API request
   * @returns Observable that emits the average recovery amount data
   */
  getAvgRecoveryAmount(
    payload: AvgRecoveryAmountPayload,
    options: {
      showLoader?: boolean;
      cache?: boolean | number;
      showErrorToaster?: boolean;
    } = {}
  ): Observable<AvgRecoveryAmountAPI> {
    const {
      showLoader = false,
      cache = false,
      showErrorToaster = true,
    } = options;

    const cacheTTL = typeof cache === 'number' ? cache : this.DEFAULT_CACHE_TTL;

    const cleanPayload = Object.entries(payload)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .reduce((obj, [key, value]) => {
        obj[key as keyof any] = value as any;
        return obj;
      }, {} as any);

    const { config, productServerName } = this.getProductContext();

    return this.apiService
      .post<AvgRecoveryAmountAPI>(
        `${this.BASE_ENDPOINT}/avg_recovery_amount`,
        { ...cleanPayload, ...config.payloadExtras },
        {
          addAccessToken: config.requiresAccessToken,
          cache: cache ? cacheTTL : false,
          productServerName,
          showLoader,
          showErrorToaster,
          errorMessage: 'Failed to retrieve average recovery amount data',
        }
      )
      .pipe(
        map((response) => {
          return this.mapResponse(response);
        }),
        catchError((error: any) => {
          const errorMessage =
            error?.message ||
            error?.error ||
            'An unexpected error occurred while retrieving average recovery amount data';
          console.error('Error retrieving average recovery amount data:', error);
          return throwError(() => ({
            ...error,
            message: errorMessage,
            originalError: error,
          }));
        })
      );
  }

  /**
   * Retrieves average processing time from scheduled slot to execution completion.
   * Shows average time over time.
   * 
   * @param payload - Request payload with date range, timezone, unit, is_dummy, outputFormat
   * @param options - Optional configuration for the API request
   * @returns Observable that emits the processing time data
   */
  getProcessingTime(
    payload: ProcessingTimePayload,
    options: {
      showLoader?: boolean;
      cache?: boolean | number;
      showErrorToaster?: boolean;
    } = {}
  ): Observable<ProcessingTimeAPI> {
    const {
      showLoader = false,
      cache = false,
      showErrorToaster = true,
    } = options;

    const cacheTTL = typeof cache === 'number' ? cache : this.DEFAULT_CACHE_TTL;

    const cleanPayload = Object.entries(payload)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .reduce((obj, [key, value]) => {
        obj[key as keyof any] = value as any;
        return obj;
      }, {} as any);

    if (!cleanPayload.outputFormat) {
      cleanPayload.outputFormat = 'db';
    }

    const { config, productServerName } = this.getProductContext();

    return this.apiService
      .post<ProcessingTimeAPI>(
        `${this.BASE_ENDPOINT}/processing_time`,
        { ...cleanPayload, ...config.payloadExtras },
        {
          addAccessToken: config.requiresAccessToken,
          cache: cache ? cacheTTL : false,
          productServerName,
          showLoader,
          showErrorToaster,
          errorMessage: 'Failed to retrieve processing time data',
        }
      )
      .pipe(
        map((response) => {
          response = this.mapResponse(response);
          if (!response.stats || !Array.isArray(response.records)) {
            throw new Error('Invalid response structure: missing stats or records');
          }
          return response;
        }),
        catchError((error: any) => {
          const errorMessage =
            error?.message ||
            error?.error ||
            'An unexpected error occurred while retrieving processing time data';
          console.error('Error retrieving processing time data:', error);
          return throwError(() => ({
            ...error,
            message: errorMessage,
            originalError: error,
          }));
        })
      );
  }

  /**
   * Retrieves detailed performance metrics for a specific admin.
   * Includes gap analysis, success/failure breakdown, creation trends, far future alerts, and contract recovery efficiency.
   * 
   * @param payload - Request payload with admin_id, date range, timezone, is_dummy
   * @param options - Optional configuration for the API request
   * @returns Observable that emits the admin detail data
   */
  getAdminDetail(
    payload: AdminDetailPayload,
    options: {
      showLoader?: boolean;
      cache?: boolean | number;
      showErrorToaster?: boolean;
    } = {}
  ): Observable<AdminDetailAPI> {
    const {
      showLoader = false,
      cache = false,
      showErrorToaster = true,
    } = options;

    const cacheTTL = typeof cache === 'number' ? cache : this.DEFAULT_CACHE_TTL;

    const cleanPayload = Object.entries(payload)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .reduce((obj, [key, value]) => {
        obj[key as keyof any] = value as any;
        return obj;
      }, {} as any);

    const { config, productServerName } = this.getProductContext();

    return this.apiService
      .post<AdminDetailAPI>(
        `${this.BASE_ENDPOINT}/admin_detail`,
        { ...cleanPayload, ...config.payloadExtras },
        {
          addAccessToken: config.requiresAccessToken,
          cache: cache ? cacheTTL : false,
          productServerName,
          showLoader,
          showErrorToaster,
          errorMessage: 'Failed to retrieve admin detail data',
        }
      )
      .pipe(
        map((response) => {
          return this.mapResponse(response);
        }),
        catchError((error: any) => {
          const errorMessage =
            error?.message ||
            error?.error ||
            'An unexpected error occurred while retrieving admin detail data';
          console.error('Error retrieving admin detail data:', error);
          return throwError(() => ({
            ...error,
            message: errorMessage,
            originalError: error,
          }));
        })
      );
  }

  /**
   * Retrieves summary statistics about duplicate arrangements and deletions.
   * Returns overall statistics about duplicate arrangements and deletions within a specified time range.
   * 
   * @param payload - Request payload with date range, timezone, and is_dummy
   * @param options - Optional configuration for the API request
   * @returns Observable that emits the duplicates summary stats data
   */
  getDuplicatesSummaryStats(
    payload: DuplicatesSummaryStatsPayload,
    options: {
      showLoader?: boolean;
      cache?: boolean | number;
      showErrorToaster?: boolean;
    } = {}
  ): Observable<DuplicatesSummaryStatsAPI> {
    const {
      showLoader = false,
      cache = false,
      showErrorToaster = true,
    } = options;

    const cacheTTL = typeof cache === 'number' ? cache : this.DEFAULT_CACHE_TTL;

    const cleanPayload = {
      start_time: payload.start_time,
      end_time: payload.end_time,
      timezone: payload.timezone,
      is_dummy: payload.is_dummy ?? 0,
    };

    const { config, productServerName } = this.getProductContext();

    return this.apiService
      .post<DuplicatesSummaryStatsAPI>(
        `${this.BASE_ENDPOINT}/duplicates/summary_stats`,
        { ...cleanPayload, ...config.payloadExtras },
        {
          addAccessToken: config.requiresAccessToken,
          cache: cache ? cacheTTL : false,
          productServerName,
          showLoader,
          showErrorToaster,
          errorMessage: 'Failed to retrieve duplicates summary stats',
        }
      )
      .pipe(
        map((response) => {
          return this.mapResponse(response);
        }),
        catchError((error: any) => {
          const errorMessage =
            error?.message ||
            error?.error ||
            'An unexpected error occurred while retrieving duplicates summary stats';
          console.error('Error retrieving duplicates summary stats:', error);
          return throwError(() => ({
            ...error,
            message: errorMessage,
            originalError: error,
          }));
        })
      );
  }

  /**
   * Retrieves duplicate and deletion statistics broken down by admin.
   * Returns statistics showing which admins created duplicates and deletions. Optionally includes detailed records.
   * 
   * @param payload - Request payload with date range, timezone, is_dummy, and include_records
   * @param options - Optional configuration for the API request
   * @returns Observable that emits the duplicates by admin data
   */
  getDuplicatesByAdmin(
    payload: DuplicatesByAdminPayload,
    options: {
      showLoader?: boolean;
      cache?: boolean | number;
      showErrorToaster?: boolean;
    } = {}
  ): Observable<DuplicatesByAdminAPI> {
    const {
      showLoader = false,
      cache = false,
      showErrorToaster = true,
    } = options;

    const cacheTTL = typeof cache === 'number' ? cache : this.DEFAULT_CACHE_TTL;

    const cleanPayload = {
      start_time: payload.start_time,
      end_time: payload.end_time,
      timezone: payload.timezone,
      is_dummy: payload.is_dummy ?? 0,
      include_records: payload.include_records ?? false,
    };

    const { config, productServerName } = this.getProductContext();

    return this.apiService
      .post<DuplicatesByAdminAPI>(
        `${this.BASE_ENDPOINT}/duplicates/by_admin`,
        { ...cleanPayload, ...config.payloadExtras },
        {
          addAccessToken: config.requiresAccessToken,
          cache: cache ? cacheTTL : false,
          productServerName,
          showLoader,
          showErrorToaster,
          errorMessage: 'Failed to retrieve duplicates by admin data',
        }
      )
      .pipe(
        map((response) => {
          response = this.mapResponse(response);
          if (!response.stats || !Array.isArray(response.records)) {
            throw new Error('Invalid response structure: missing stats or records');
          }
          return response;
        }),
        catchError((error: any) => {
          const errorMessage =
            error?.message ||
            error?.error ||
            'An unexpected error occurred while retrieving duplicates by admin data';
          console.error('Error retrieving duplicates by admin data:', error);
          return throwError(() => ({
            ...error,
            message: errorMessage,
            originalError: error,
          }));
        })
      );
  }

  /**
   * Retrieves time-series trend data showing duplicates and deletions over time.
   * Automatically determines the best time unit (day, week, month) based on the date range.
   * 
   * @param payload - Request payload with date range, timezone, unit, is_dummy, include, outputFormat
   * @param options - Optional configuration for the API request
   * @returns Observable that emits the duplicates trend data
   */
  getDuplicatesTrend(
    payload: DuplicatesTrendPayload,
    options: {
      showLoader?: boolean;
      cache?: boolean | number;
      showErrorToaster?: boolean;
    } = {}
  ): Observable<DuplicatesTrendAPI> {
    const {
      showLoader = false,
      cache = false,
      showErrorToaster = true,
    } = options;

    const cacheTTL = typeof cache === 'number' ? cache : this.DEFAULT_CACHE_TTL;

    const cleanPayload: any = {
      start_time: payload.start_time,
      end_time: payload.end_time,
      timezone: payload.timezone,
      is_dummy: payload.is_dummy ?? 0,
    };

    if (payload.unit !== undefined && payload.unit !== null) {
      cleanPayload.unit = payload.unit;
    }

    if (payload.include !== undefined && Array.isArray(payload.include)) {
      cleanPayload.include = payload.include;
    }

    if (payload.outputFormat !== undefined && payload.outputFormat !== null) {
      cleanPayload.outputFormat = payload.outputFormat;
    } else {
      cleanPayload.outputFormat = 'db';
    }

    const { config, productServerName } = this.getProductContext();

    return this.apiService
      .post<DuplicatesTrendAPI>(
        `${this.BASE_ENDPOINT}/duplicates/trend`,
        { ...cleanPayload, ...config.payloadExtras },
        {
          addAccessToken: config.requiresAccessToken,
          cache: cache ? cacheTTL : false,
          productServerName,
          showLoader,
          showErrorToaster,
          errorMessage: 'Failed to retrieve duplicates trend data',
        }
      )
      .pipe(
        map((response) => {
          response = this.mapResponse(response);
          if (!Array.isArray(response.records)) {
            throw new Error('Invalid response structure: missing records');
          }
          return response;
        }),
        catchError((error: any) => {
          const errorMessage =
            error?.message ||
            error?.error ||
            'An unexpected error occurred while retrieving duplicates trend data';
          console.error('Error retrieving duplicates trend data:', error);
          return throwError(() => ({
            ...error,
            message: errorMessage,
            originalError: error,
          }));
        })
      );
  }

  /**
   * Retrieves detailed information about each contract, including all arrangement history entries,
   * active arrangements, and bucket categorization. Supports filtering by various criteria.
   * 
   * @param payload - Request payload with date range, timezone, is_dummy, and filters
   * @param options - Optional configuration for the API request
   * @returns Observable that emits the contract details data
   */
  getContractDetails(
    payload: ContractDetailsPayload,
    options: {
      showLoader?: boolean;
      cache?: boolean | number;
      showErrorToaster?: boolean;
    } = {}
  ): Observable<ContractDetailsAPI> {
    const {
      showLoader = false,
      cache = false,
      showErrorToaster = true,
    } = options;

    const cacheTTL = typeof cache === 'number' ? cache : this.DEFAULT_CACHE_TTL;

    const cleanPayload: any = {
      start_time: payload.start_time,
      end_time: payload.end_time,
      timezone: payload.timezone,
      is_dummy: payload.is_dummy ?? 0,
    };

    if (payload.filters !== undefined && payload.filters !== null) {
      cleanPayload.filters = payload.filters;
    }

    const { config, productServerName } = this.getProductContext();

    return this.apiService
      .post<ContractDetailsAPI>(
        `${this.BASE_ENDPOINT}/duplicates/contract_details`,
        { ...cleanPayload, ...config.payloadExtras },
        {
          addAccessToken: config.requiresAccessToken,
          cache: cache ? cacheTTL : false,
          productServerName,
          showLoader,
          showErrorToaster,
          errorMessage: 'Failed to retrieve contract details data',
        }
      )
      .pipe(
        map((response) => {
          response = this.mapResponse(response);
          // Adjusting check: mapResponse already returns originalResponse.data
          // So if originalResponse was { is_error: 0, data: { contracts: [...] } }
          // mappedResponse is { contracts: [...] }
          if (!response || !Array.isArray(response.contracts)) {
            throw new Error('Invalid response structure: missing contracts array');
          }
          return response;
        }),
        catchError((error: any) => {
          const errorMessage =
            error?.message ||
            error?.error ||
            'An unexpected error occurred while retrieving contract details data';
          console.error('Error retrieving contract details data:', error);
          return throwError(() => ({
            ...error,
            message: errorMessage,
            originalError: error,
          }));
        })
      );
  }

  /**
   * Clears the cache for all arrangement cron related endpoints.
   * Useful when data needs to be refreshed immediately.
   * 
   * @param endpoint - Optional specific endpoint to clear cache for
   */
  clearCache(endpoint?: string): void {
    if (endpoint) {
      this.apiService.clearCache(`${this.BASE_ENDPOINT}/${endpoint}`);
    } else {
      // Clear cache for all arrangement cron endpoints
      const endpoints = [
        'most_recent',
        'scheduled_today',
        'pending_by_gap',
        'creation_trend',
        'scheduled_trend',
        'payment_outcomes_by_slot',
        'success_rate_trend',
        'recovery_status',
        'by_admin',
        'recovery_rate',
        'avg_recovery_amount',
        'processing_time',
        'admin_detail',
        'duplicates/summary_stats',
        'duplicates/by_admin',
        'duplicates/trend',
        'duplicates/contract_details',
      ];
      endpoints.forEach((ep) => {
        this.apiService.clearCache(`${this.BASE_ENDPOINT}/${ep}`);
      });
    }
  }
}
